// WOENavigationHeader.jsx - Simple version without external CSS
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './WOENavigationHeader.css';

const WOENavigationHeader = ({
    currentSlide,
    onSlideChange,
    customerInfo,
    isIsraelUser = false,
    wigDetailsData,
    wigRoomsData,
    paymentData,
    onReset,
    canClearState,
    // New multi-order props
    activeOrders = [],
    currentOrderId,
    onSwitchOrder,
    onCreateOrder,
    onCloseOrder,
    onDuplicateOrder,
    getOrderDisplayInfo
}) => {
    const safeWigRoomsData = Array.isArray(wigRoomsData) ? wigRoomsData : [];

    const [showNewOrderModal, setShowNewOrderModal] = useState(false);
    const [newOrderData, setNewOrderData] = useState({
        orderType: '',
        customerName: ''
    });
    

    const slides = [
        { id: 1, title: "Customer Info", icon: "👤" },
        { id: 2, title: "Wig Details", icon: "💇‍♀️" },
        { id: 3, title: "Room Tasks", icon: "🏢" },
        { id: 4, title: "Payment", icon: "💳" }
    ];

    // Determine if a slide can be accessed
    
    const canAccessSlide = (slideId) => {
        return true;
    };

    // Get completion status
    const getSlideStatus = (slideId) => {
        switch (slideId) {
            case 1:
                if (!customerInfo?.customerName || !customerInfo?.orderType) return 'incomplete';
                return 'complete';
            case 2:
                // Check if slide 1 is complete first
                if (!customerInfo?.customerName || !customerInfo?.orderType) return 'locked';
                // Check if we can access it
                return canAccessSlide(slideId) ? 'complete' : 'incomplete';
            case 3:
                // Check if slide 2 is complete - must have selected items (except Accessory)
                if (customerInfo?.orderType !== 'Accessory') {
                    const hasItems = wigDetailsData?.selectedItems?.length > 0;
                    if (!hasItems) return 'locked';
                }
                // Check if we can access it
                return canAccessSlide(slideId) ? 'complete' : 'incomplete';
            case 4:
                // Check if slide 3 is complete - must have room tasks (except Accessory)
                if (customerInfo?.orderType !== 'Accessory') {
                    if (!wigRoomsData || wigRoomsData.length === 0) return 'locked';
                }
                // Check for actual payment completion
                return paymentData?.paymentSummary?.isComplete ? 'complete' :
                    canAccessSlide(slideId) ? 'incomplete' : 'incomplete';
            default:
                return 'incomplete';
        }
    };

    const handleSlideClick = (slideId) => {
        const status = getSlideStatus(slideId);

        // Don't navigate if clicking current slide
        if (slideId === currentSlide) return;

        // Show helpful message if slide is locked
        if (status === 'locked') {
            if (slideId === 2) {
                alert('⚠️ Please complete customer information first.');
            } else if (slideId === 3) {
                alert('⚠️ Please select at least one wig item first.');
            } else if (slideId === 4) {
                alert('⚠️ Please add at least one room task first.');
            }
            return;
        }

        // Standard navigation check
        if (canAccessSlide(slideId)) {
            onSlideChange(slideId);
        }
    };

      // Multi-order handlers
    const handleCreateOrder = () => {
        onCreateOrder(newOrderData.orderType, newOrderData.customerName);
        setShowNewOrderModal(false);
        setNewOrderData({ orderType: '', customerName: '' });
    };

    const handleTabClick = (orderId) => {
        if (orderId !== currentOrderId) {
            onSwitchOrder(orderId);
        }
    };

    const handleCloseTab = (e, orderId) => {
        e.stopPropagation(); // Prevent tab switching
        onCloseOrder(orderId);
    };

    const handleDuplicate = (e, orderId) => {
        e.stopPropagation();
        const customerName = prompt('Enter customer name for duplicate order (optional):');
        if (customerName !== null) { // User didn't cancel
            onDuplicateOrder(orderId, customerName);
        }
    };

    return (
        <div className="woe-navigation-wrapper">
            {/* Order Tabs Section - Only show if multiple orders */}
            {activeOrders.length > 0 && (
                <div className="order-tabs-container">
                    <div className="order-tabs-wrapper">
                        {/* Order Tabs */}
                        <div className="order-tabs">
                            {activeOrders.map((order) => {
                                const displayInfo = getOrderDisplayInfo(order);
                                const isActive = order.id === currentOrderId;
                                
                                return (
                                    <div
                                        key={order.id}
                                        className={`order-tab ${isActive ? 'active' : ''} ${displayInfo.hasUnsavedChanges ? 'unsaved' : ''}`}
                                        onClick={() => handleTabClick(order.id)}
                                        title={`${displayInfo.subtitle}\nSlide ${displayInfo.currentSlide}/4\n${displayInfo.hasUnsavedChanges ? 'Unsaved changes' : 'Saved'}`}
                                    >
                                        <div className="tab-content">
                                            <div className="tab-title">
                                                {displayInfo.title}
                                                {displayInfo.hasUnsavedChanges && <span className="unsaved-dot">●</span>}
                                            </div>
                                            <div className="tab-subtitle">
                                                {displayInfo.subtitle}
                                            </div>
                                        </div>
                                        
                                        <div className="tab-actions">
                                            {/* Duplicate button */}
                                            {/* <button
                                                className="tab-action-btn duplicate-btn"
                                                onClick={(e) => handleDuplicate(e, order.id)}
                                                title="Duplicate order"
                                            >
                                                📋
                                            </button> */}
                                            
                                            {/* Close button - only show if more than 1 order */}
                                            {activeOrders.length > 1 && (
                                                <button
                                                    className="tab-action-btn close-btn"
                                                    onClick={(e) => handleCloseTab(e, order.id)}
                                                    title="Close order"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* New Order Button */}
                        <button
                            className="new-order-btn"
                            onClick={() => setShowNewOrderModal(true)}
                            title="Create new order"
                        >
                            + New Order
                        </button>
                    </div>

                    {/* Order Counter */}
                    <div className="order-counter">
                        {activeOrders.length} active order{activeOrders.length !== 1 ? 's' : ''}
                    </div>
                </div>
            )}

            {/* Main Navigation Header */}
            <div className="main-navigation-header">
                <div className="nav-content-wrapper">
                     <Link to="/wig-preview" className="new-order-btn" title="Wig Preview Tool">
                            Wig Preview
                        </Link>
                    {/* Title Section */}
                    <div className="nav-title-section">
                        <h2>Wig Order Entry</h2>
                        <div className="nav-subtitle">
                            Order #{customerInfo?.orderNo || 'New'}
                            {isIsraelUser && <span className="location-badge">🇮🇱 Israel</span>}
                        </div>
                    </div>

                    {/* Navigation Steps */}
                    <div className="nav-steps-container">
                        {slides.map((slide, index) => {
                            const isActive = currentSlide === slide.id;
                            const canAccess = canAccessSlide(slide.id);
                            const status = getSlideStatus(slide.id);
                            const isClickable = canAccess && !isActive;

                            return (
                                <React.Fragment key={slide.id}>
                                    <div
                                        onClick={() => handleSlideClick(slide.id)}
                                        className={`nav-step ${isActive ? 'active' : ''} ${canAccess ? 'accessible' : 'locked'} ${isClickable ? 'clickable' : ''}`}
                                    >
                                        {/* Step Number/Status */}
                                        <div className={`step-indicator ${status}`}>
                                            {status === 'complete' ? '✓' : slide.id}
                                        </div>

                                        {/* Icon and Title */}
                                        <div className="step-content">
                                            <div className="step-icon">{slide.icon}</div>
                                            <div className="step-title">{slide.title}</div>
                                        </div>

                                        {/* Status Indicator */}
                                        <div className="step-status">
                                            {status === 'complete' && '✅'}
                                            {status === 'incomplete' && canAccess && '⏳'}
                                            {!canAccess && '🔒'}
                                        </div>
                                    </div>

                                    {/* Connector */}
                                    {index < slides.length - 1 && (
                                        <div className={`step-connector ${status === 'complete' ? 'complete' : ''}`} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    {/* Progress Indicator */}
                    <div className="nav-progress-section">
                        <div>Step {currentSlide} of {slides.length}</div>
                        <div className="progress-percentage">
                            {Math.round((currentSlide / slides.length) * 100)}% Complete
                        </div>
                    </div>
                </div>
            </div>

            {/* New Order Modal */}
            {showNewOrderModal && (
                <div className="modal-overlay">
                    <div className="new-order-modal">
                        <h3>Create New Order</h3>
                      
                            <div className="form-group">
                                <label>Order Type (optional):</label>
                                <select
                                    value={newOrderData.orderType}
                                    onChange={(e) => setNewOrderData(prev => ({ ...prev, orderType: e.target.value }))}
                                >
                                    <option value="">Select Order Type</option>
                                    <option value="New Wig">New Wig</option>
                                    <option value="New Sale Wig">New Sale Wig</option>
                                    <option value="Aidel New Wig">Aidel New Wig</option>
                                    <option value="Wash & Set">Wash & Set</option>
                                    <option value="Wig Repair">Wig Repair</option>
                                    <option value="Aidel Wig Repair">Aidel Wig Repair</option>
                                    <option value="Consultation">Consultation</option>
                                    <option value="Accessory">Accessory</option>
                                    <option value="Recut">Recut</option>
                                </select>
                            </div>
                            
                            {/* <div className="form-group">
                                <label>Customer Name (optional):</label>
                                <input
                                    type="text"
                                    value={newOrderData.customerName}
                                    onChange={(e) => setNewOrderData(prev => ({ ...prev, customerName: e.target.value }))}
                                    placeholder="Enter customer name"
                                    autoFocus
                                />
                            </div> */}
                       
                        
                        <div className="modal-actions">
                            <button
                                className="btn-secondary"
                                onClick={() => {
                                    setShowNewOrderModal(false);
                                    setNewOrderData({ orderType: '', customerName: '' });
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={handleCreateOrder}
                            >
                                Create Order
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WOENavigationHeader;